import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Trophy, Medal, Award } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LeaderboardPage() {
  const navigate = useNavigate();
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('session_token');
    axios.get(`${BACKEND_URL}/api/leaderboard`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setLeaders(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const getRankIcon = (index) => {
    if (index === 0) return <Trophy className="h-6 w-6 text-amber-400" />;
    if (index === 1) return <Medal className="h-6 w-6 text-slate-400" />;
    if (index === 2) return <Medal className="h-6 w-6 text-amber-700" />;
    return <span className="w-6 h-6 flex items-center justify-center text-sm font-bold text-slate-500">{index + 1}</span>;
  };

  const getRankBg = (index) => {
    if (index === 0) return 'bg-gradient-to-r from-amber-50 to-amber-100 border-amber-300';
    if (index === 1) return 'bg-gradient-to-r from-slate-50 to-slate-100 border-slate-300';
    if (index === 2) return 'bg-gradient-to-r from-orange-50 to-orange-100 border-orange-300';
    return 'bg-white border-slate-200';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-slate-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-6" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" /> Voltar
        </Button>

        <Card className="hero-card mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-2xl">
              <Trophy className="h-7 w-7 text-amber-400" /> Ranking de Pontos
            </CardTitle>
            <p className="text-slate-200 text-sm mt-1">Os usuários mais ativos do ClubdoCupom</p>
          </CardHeader>
        </Card>

        <div className="space-y-3">
          {leaders.length === 0 ? (
            <Card className="bg-white border border-slate-200">
              <CardContent className="py-12 text-center">
                <Award className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Nenhum usuário no ranking ainda. Valide uma compra para aparecer aqui!</p>
              </CardContent>
            </Card>
          ) : (
            leaders.map((user, index) => (
              <Card key={user.user_id || index} className={`border-2 transition-all hover:shadow-md ${getRankBg(index)}`} data-testid={`rank-${index}`}>
                <CardContent className="py-4">
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0">
                      {getRankIcon(index)}
                    </div>
                    <div className="flex-shrink-0">
                      {user.picture ? (
                        <img src={user.picture} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center border-2 border-white shadow-sm">
                          <span className="text-slate-600 font-bold">{(user.name || 'U')[0]}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-slate-900 truncate">{user.name}</p>
                      <p className="text-xs text-slate-500">Nível {user.gamification_level || 1}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xl font-black text-slate-900">{user.gamification_points || 0}</p>
                      <p className="text-xs text-slate-500">pontos</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
